import { addHours, startOfHour } from "date-fns";
import {
  useContext,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
} from "react";
import { useTranslation } from "react-i18next";

import { TimeSpanInputContext } from "@/contexts/TimeSpanInputContext";
import { htmlDate } from "@/fp/htmlDate";
import { useCurrentTime } from "@/hooks/useCurrentTime";

import { ActionBox } from "./ActionBox";
import { Button } from "./Button";
import { IconButton } from "./IconButton";
import { Input, InputContainer } from "./Input";

export function StartTimeInput({
  defaultValue,
  className,
  onChange,
  ...props
}: { defaultValue?: string } & Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  "defaultValue"
>) {
  const [, setStartTime] = useContext(TimeSpanInputContext);
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [isShowingDateField, setIsShowingDateField] = useState(!!defaultValue);

  const now = useCurrentTime("@hourly");

  useEffect(() => {
    if (defaultValue) setStartTime(new Date(defaultValue));
  }, []); // oxlint-disable-line exhaustive-effect-dependencies

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const date = event.currentTarget.value ? new Date(event.currentTarget.value) : null;
    setStartTime(date);
    onChange?.(event);
  }

  function clear() {
    if (input.current) {
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!.set!.call(input.current, "");
      input.current.dispatchEvent(new Event("change", { bubbles: true }));
    }
    setIsShowingDateField(false);
  }

  function setDate(date: Date) {
    if (input.current) {
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")!.set!.call(input.current, htmlDate(date));
      input.current.dispatchEvent(new Event("change", { bubbles: true }));
      setIsShowingDateField(true);
    }
  }

  return (
    <InputContainer className={className}>
      <Input
        ref={input}
        type="datetime-local"
        className={isShowingDateField ? undefined : "hidden"}
        defaultValue={defaultValue}
        onChange={handleChange}
        {...props}
      />
      {isShowingDateField && <IconButton type="button" name="trash" onClick={clear} />}
      {!isShowingDateField && (
        <ActionBox className="grow">
          <Button type="button" onClick={() => setDate(new Date())}>
            {t("now")}
          </Button>
          <Button type="button" onClick={() => setDate(addHours(startOfHour(new Date()), 1))}>
            {t("at-hour", { date: addHours(startOfHour(now), 1) })}
          </Button>
          <Button type="button" onClick={() => setDate(addHours(startOfHour(now), 24))}>
            {t("tomorrow")}
          </Button>
          <Button className="ml-auto" type="button" onClick={() => setIsShowingDateField(true)}>
            {t("set-manually")}
          </Button>
        </ActionBox>
      )}
    </InputContainer>
  );
}
